/* eslint-disable no-empty */
import {
  PracticeTimesSchedule,
  PracticeTimesScheduleEntry,
  RequestBasePostPracticeTimesSchedule,
  RequestBasePostPracticeTimesScheduleEntry,
  ResponseBaseGetPracticeTimesSchedulesCurrentAndUpcoming,
  ResponseBaseGetPracticeTimesSchedulesUpcoming,
  ResponseBasePostPracticeTimesSchedule,
  ResponseBasketballGetPracticeTimeScheduleOverview,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';
import { actions } from './basketball-practice-times.slice';

export const basketballPracticeTimesApi = createApi({
  reducerPath: 'apiBasketballPracticeTimes',
  baseQuery: API.baseQuery,
  tagTypes: [
    'CurrentAndUpcomingPracticeTimesSchedules',
    'UpcomingPracticeTimesSchedules',
  ],
  endpoints: (builder) => ({
    /**************************************************************************
     *                              ARTICLES
     *************************************************************************/
    fetchPracticeTimesSchedules: builder.query<PracticeTimesSchedule[], void>({
      query: (request) => 'practice-times-schedules',
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.length > 0) {
            const firstSchedule = data[0];
            dispatch(actions.setActiveScheduleId(firstSchedule.id));
          }
        } catch (error: unknown) {}
      },
    }),
    fetchCurrentAndUpcomingPracticeTimesSchedules: builder.query<
      ResponseBaseGetPracticeTimesSchedulesCurrentAndUpcoming,
      void
    >({
      query: (request) => 'practice-times-schedules/current-and-upcoming',
      providesTags: ['CurrentAndUpcomingPracticeTimesSchedules'],
    }),
    fetchUpcomingPracticeTimesSchedules: builder.query<
      ResponseBaseGetPracticeTimesSchedulesUpcoming,
      void
    >({
      query: (request) => 'practice-times-schedules/upcoming',
      providesTags: ['UpcomingPracticeTimesSchedules'],
    }),
    fetchBasketballPracticeTimesDraftOverview: builder.query<
      ResponseBasketballGetPracticeTimeScheduleOverview,
      number
    >({
      query: (id) => `practice-times-schedules/${id}/basketball`,
    }),
    createPracticeTimesSchedule: builder.mutation<
      ResponseBasePostPracticeTimesSchedule,
      RequestBasePostPracticeTimesSchedule
    >({
      query: (request) => ({
        url: 'practice-times-schedules',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [
        'CurrentAndUpcomingPracticeTimesSchedules',
        'UpcomingPracticeTimesSchedules',
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            basketballPracticeTimesApi.util.updateQueryData(
              'fetchPracticeTimesSchedules',
              undefined,
              (draft) => {
                // check if response id is already in the list
                const existingSchedule = draft?.find(
                  (schedule) => schedule.id === responseData.id
                );
                if (existingSchedule) {
                  Object.assign(existingSchedule, responseData);
                } else {
                  // add new schedule
                  draft?.push(responseData);
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    deletePracticeTimesSchedule: builder.mutation<void, PracticeTimesSchedule>({
      query: (request) => ({
        url: `practice-times-schedules/${request.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'CurrentAndUpcomingPracticeTimesSchedules',
        'UpcomingPracticeTimesSchedules',
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            basketballPracticeTimesApi.util.updateQueryData(
              'fetchPracticeTimesSchedules',
              undefined,
              (draft) => draft.filter((schedule) => schedule.id !== request.id)
            )
          );
        } catch (error: unknown) {}
      },
    }),
    markPracticeTimesScheduleAsDefault: builder.mutation<
      void,
      PracticeTimesSchedule
    >({
      query: (request) => ({
        url: `practice-times-schedules/${request.id}/mark-as-default`,
        method: 'POST',
      }),
      invalidatesTags: [
        'CurrentAndUpcomingPracticeTimesSchedules',
        'UpcomingPracticeTimesSchedules',
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            basketballPracticeTimesApi.util.updateQueryData(
              'fetchPracticeTimesSchedules',
              undefined,
              (draft) =>
                draft.map((schedule) => ({
                  ...schedule,
                  default: schedule.id === request.id,
                }))
            )
          );
        } catch (error: unknown) {}
      },
    }),
    createPracticeTimesScheduleEntry: builder.mutation<
      PracticeTimesScheduleEntry,
      {
        scheduleId: number;
        request: RequestBasePostPracticeTimesScheduleEntry;
      }
    >({
      query: (request) => ({
        url: `practice-times-schedules/${request.scheduleId}/entries`,
        method: 'POST',
        body: request.request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            basketballPracticeTimesApi.util.updateQueryData(
              'fetchBasketballPracticeTimesDraftOverview',
              request.scheduleId,
              (draft) => {
                const teamIds = responseData?.basketball_teams?.map(
                  (t) => t.id
                );

                draft.teams?.map((team) => {
                  if (teamIds?.includes(team.id)) {
                    if (request.request.id) {
                      team.entries = team.entries?.map((entry) =>
                        entry.id === request.request.id ? responseData : entry
                      );
                    } else {
                      team.entries?.push(responseData);
                    }
                  }
                  return team;
                });
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    deletePracticeTimesScheduleEntry: builder.mutation<
      void,
      PracticeTimesScheduleEntry
    >({
      query: (request) => ({
        url: `practice-times-schedules/${request.practice_times_schedule_id}/entries/${request.id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            basketballPracticeTimesApi.util.updateQueryData(
              'fetchBasketballPracticeTimesDraftOverview',
              parseInt(`${request.practice_times_schedule_id}`),
              (draft) => {
                if (draft?.teams) {
                  const teamIds = request.basketball_teams?.map((t) => t.id);
                  draft.teams = draft?.teams?.map((team) => {
                    if (teamIds?.includes(team.id)) {
                      team.entries = team.entries?.filter(
                        (entry) => entry.id !== request.id
                      );
                    }
                    return team;
                  });
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
  }), // end of endpoints
});

// Export hooks for usage in functional components
export const {
  useFetchPracticeTimesSchedulesQuery,
  useFetchCurrentAndUpcomingPracticeTimesSchedulesQuery,
  useFetchUpcomingPracticeTimesSchedulesQuery,
  useFetchBasketballPracticeTimesDraftOverviewQuery,
  useCreatePracticeTimesScheduleEntryMutation,
  useCreatePracticeTimesScheduleMutation,
  useMarkPracticeTimesScheduleAsDefaultMutation,
  useDeletePracticeTimesScheduleMutation,
  useDeletePracticeTimesScheduleEntryMutation,
  reducerPath,
  reducer,
  middleware,
} = basketballPracticeTimesApi;
